import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import { Button, Dropdown, Icon, Message, Table } from 'semantic-ui-react';

import ValidatedCurrency from '../../../../components/common/validatedCurrency';
import * as templateHelper from '../../../../components/template/templateHelper';
import { TARGET_VALIDATION } from '../../../../constants/validationModels';
import { selectIsFeatureFlagExplicitlyEnabled } from '../../../../modules/featureFlags/featureFlagSelectors';
import { FeatureFlagKeys } from '../../../../modules/featureFlags/featureFlagTypes';
import {
  getAmbassadorProgramCurrencies,
  normaliseCurrency,
  orderCurrencyTargets,
  removeCurrencyTarget,
  upsertCurrencyTarget
} from '../../helpers/currencyTargets';

import styles from './templateCurrencyTargets.module.css';

const i18nBase = 'programs.template.page.setup.currency-targets';

const amountValidation = { required: true, ...TARGET_VALIDATION };

interface TemplateCurrencyTargetsProps {
  id: string;
  /** Template path holding the list of `{ currency, fundraiserMinimumTargetAmount }`. */
  path: string;
  /** Template path holding the event currency code. */
  eventCurrencyPath: string;
  /** Template path holding the event-currency amount the API still reads. */
  mirrorPath: string;
  /** Currencies already saved on the program. They cannot be removed. */
  lockedCurrencies?: string[];
  label: string;
  subtext: string;
  /** Read by the template builder, not by this component. */
  validation?: Record<string, unknown>;
  onChange: (path: string, value: unknown) => void;
}

const parseAmount = (raw: string): number | null => {
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

export const TemplateCurrencyTargets: React.FC<
  TemplateCurrencyTargetsProps
> = ({
  id,
  path,
  eventCurrencyPath,
  mirrorPath,
  lockedCurrencies = [],
  label,
  subtext,
  onChange
}) => {
  const targets = useSelector((state: any) =>
    templateHelper.getValue(state.templateBuilderEx, path)
  );
  const eventCurrency = normaliseCurrency(
    useSelector((state: any) =>
      templateHelper.getValue(state.templateBuilderEx, eventCurrencyPath)
    )
  );
  const mirrorValue = useSelector((state: any) =>
    templateHelper.getValue(state.templateBuilderEx, mirrorPath)
  );
  const isUkCharityEnabled = useSelector((state: any) =>
    selectIsFeatureFlagExplicitlyEnabled(state, FeatureFlagKeys.UkCharity)
  );

  const rows = useMemo(
    () => orderCurrencyTargets(targets, eventCurrency),
    [targets, eventCurrency]
  );
  const eventRow = rows.find((row) => row.currency === eventCurrency);
  const eventAmount = eventRow ? eventRow.fundraiserMinimumTargetAmount : null;
  const mirrorAmount =
    typeof mirrorValue === 'number' && !Number.isNaN(mirrorValue)
      ? mirrorValue
      : null;

  // The event currency always has a row, seeded from the scalar amount the
  // API still exposes, and the scalar always mirrors that row.
  useEffect(() => {
    if (!eventCurrency) {
      return;
    }
    if (!eventRow) {
      onChange(path, [
        {
          currency: eventCurrency,
          fundraiserMinimumTargetAmount: mirrorAmount
        },
        ...rows
      ]);
      return;
    }
    if (eventAmount !== mirrorAmount) {
      onChange(mirrorPath, eventAmount);
    }
  }, [
    eventCurrency,
    eventRow,
    eventAmount,
    mirrorAmount,
    rows,
    onChange,
    path,
    mirrorPath
  ]);

  const locked = useMemo(
    () =>
      new Set(
        [eventCurrency, ...lockedCurrencies]
          .map((code) => normaliseCurrency(code))
          .filter((code): code is string => !!code)
      ),
    [eventCurrency, lockedCurrencies]
  );

  const availableCurrencies = getAmbassadorProgramCurrencies(
    isUkCharityEnabled
  ).filter((code) => !rows.some((row) => row.currency === code));

  const onAmountChange = (currency: string, raw: string) => {
    const amount = parseAmount(raw);
    onChange(path, upsertCurrencyTarget(rows, currency, amount));
    if (currency === eventCurrency) {
      onChange(mirrorPath, amount);
    }
  };

  const onAddCurrency = (currency: string) => {
    onChange(path, upsertCurrencyTarget(rows, currency, null));
  };

  const onRemoveCurrency = (currency: string) => {
    onChange(path, removeCurrencyTarget(rows, currency));
  };

  return (
    <div className="field" id={id}>
      <label>{I18n.t(label)}</label>
      {!eventCurrency && (
        <Message info content={I18n.t(`${i18nBase}.select-event`)} />
      )}
      {eventCurrency && (
        <>
          <Table basic="very" compact className={styles.table}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {I18n.t(`${i18nBase}.currency-column`)}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {I18n.t(`${i18nBase}.amount-column`)}
                </Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row) => (
                <Table.Row key={row.currency}>
                  <Table.Cell>
                    {row.currency}
                    {row.currency === eventCurrency && (
                      <span className={styles.eventCurrencyHint}>
                        {I18n.t(`${i18nBase}.event-currency`)}
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <ValidatedCurrency
                      id={`${id}-${row.currency}`}
                      inputLabel={row.currency}
                      value={row.fundraiserMinimumTargetAmount ?? ''}
                      validation={amountValidation}
                      required
                      style={{ width: '184px' }}
                      aria-label={I18n.t(`${i18nBase}.amount-for`, {
                        currency: row.currency
                      })}
                      onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                        onAmountChange(row.currency, evt.target.value)
                      }
                    />
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    {!locked.has(row.currency) && (
                      <Button
                        type="button"
                        basic
                        icon
                        size="small"
                        aria-label={I18n.t(`${i18nBase}.remove-currency`, {
                          currency: row.currency
                        })}
                        onClick={() => onRemoveCurrency(row.currency)}
                      >
                        <Icon name="trash" aria-hidden="true" />
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          {availableCurrencies.length > 0 && (
            <div className={styles.addRow}>
              <Dropdown
                id={`${id}-add`}
                selection
                selectOnBlur={false}
                value=""
                placeholder={I18n.t(`${i18nBase}.add-currency`)}
                aria-label={I18n.t(`${i18nBase}.add-currency`)}
                options={availableCurrencies.map((code) => ({
                  key: code,
                  text: code,
                  value: code
                }))}
                onChange={(_, data) => onAddCurrency(String(data.value))}
              />
            </div>
          )}

          <div className="input-sub input-sub-bottom">{I18n.t(subtext)}</div>
          <Message
            size="small"
            list={[
              I18n.t(`${i18nBase}.change-warning`),
              I18n.t(`${i18nBase}.locked`)
            ]}
          />
        </>
      )}
    </div>
  );
};

export default TemplateCurrencyTargets;
